import { useEffect, useMemo, useState } from 'react';
import KpiCard from '../../../Common/KpiCard';
import SearchBox from "../../components/SearchComponent";
import TaskListSection from './TaskListSection';
import { FiTarget } from 'react-icons/fi';
import { RiPulseAiFill } from 'react-icons/ri';
import { GoAlertFill } from 'react-icons/go';
import { LuAlarmClock } from 'react-icons/lu';
import { LuUsers } from 'react-icons/lu';
import { useApiRequest } from "../../../hooks/useApiRequest";
import { taskManagementApi } from "../../../actions/taskManagement";
import { formatDate, getInitials } from '../../../utils';
import TaskBadge from './TaskBadge';


// Helper function to calculate days overdue
const calculateDaysOverdue = (dueDate) => {
    if (!dueDate) return 0;
    const today = new Date();
    const due = new Date(dueDate);
    const diffTime = today - due;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays > 0 ? diffDays : 0;
};

const Dashboard = () => {
    const { request } = useApiRequest();
    const [cards, setCards] = useState({ totalTasks: 0, overdueTasks: 0, dueSoonTasks: 0, completionRate: 0 });
    const [teamStatus, setTeamStatus] = useState([]);
    const [highPriority, setHighPriority] = useState([]);
    const [overdueTasks, setOverdueTasks] = useState([]);

    useEffect(() => {
        const fetchDashboard = async () => {
            await request({
                url: taskManagementApi.getDashboardData,
                showToast: false,
                onSuccess: (response) => {
                    const data = response?.data ?? {};
                    setCards(data?.cards ?? { totalTasks: 0, overdueTasks: 0, dueSoonTasks: 0, completionRate: 0 });
                    setTeamStatus(data?.teamStatus ?? []);
                    setHighPriority(data?.highPriority ?? []);
                    setOverdueTasks(data?.overdueTasks ?? []);
                }
            });
        }
        fetchDashboard();
    }, []);

    // Transform overdue tasks data
    const overdueTasksData = useMemo(() => {
        return (overdueTasks || []).map(task => ({
            id: task?.task_id ?? 0,
            title: task?.title ?? "",
            priority: task.priority || "High",
            dueDate: task?.due_date,
            daysOverdue: calculateDaysOverdue(task?.due_date),
            assignee: task?.assignee?.name || "Unassigned",
            assigneeEmail: task?.assignee?.email || "",
            status: task?.TaskStatus?.status_name || "Overdue"
        }));
    }, [overdueTasks]);

    return (
        <>
            {/* <SearchBox placeholder="Search here ..." /> */}
            <div className="mt-1">
                <div className="row g-2">
                    <div className="col-12 col-sm-6 col-md-3">
                        <KpiCard
                            icon={<FiTarget size={18} />}
                            value={`${cards.completionRate || 0}%`}
                            description={"Team Completion Rate"}
                            color="#0d6efd"
                        />
                    </div>
                    <div className="col-12 col-sm-6 col-md-3">
                        <KpiCard
                            icon={<RiPulseAiFill size={18} />}
                            value={String(cards.totalTasks || 0)}
                            description={"Total"}
                            color="#0dcaf0"
                        />
                    </div>
                    <div className="col-12 col-sm-6 col-md-3">
                        <KpiCard
                            icon={<GoAlertFill size={18} />}
                            value={String(cards.overdueTasks || 0)}
                            description={"Overdue"}
                            color="#dc3545"
                        />
                    </div>
                    <div className="col-12 col-sm-6 col-md-3">
                        <KpiCard
                            icon={<LuAlarmClock size={18} />}
                            value={String(cards.dueSoonTasks || 0)}
                            description={"Due Soon"}
                            color="#fd7e14"
                        />
                    </div>
                </div>

                {/* Example table using users */}
                <div className="mt-4">
                    <div
                        className="shadow-sm"
                        style={{
                            borderRadius: 12,
                            border: "1px solid #edf0f4",
                            background: "#fff",
                            overflow: "hidden",
                        }}
                    >
                        <div className="p-3 pb-2 d-flex align-items-center" style={{ color: "#374151" }}>
                            <LuUsers size={18} className="me-2" />
                            <h5 className="mb-0 fw-semibold">Team Status</h5>
                        </div>
                        <div className="px-3 pb-3">
                            <div className="table-responsive">
                                <table className="table align-middle mb-0">
                                    <thead>
                                        <tr style={{ borderBottom: "1px solid #e5e7eb" }}>
                                            <th className="border-0 pb-2" style={{ width: 200, color: "#374151", fontWeight: 500 }}>Users</th>
                                            <th className="border-0 pb-2 text-center" colSpan={4} style={{ color: "#374151", fontWeight: 500 }}>Tasks</th>
                                        </tr>
                                        <tr style={{ borderBottom: "1px solid #e5e7eb" }}>
                                            <th className="border-0 pt-1 pb-2"></th>
                                            <th className="border-0 pt-1 pb-2 text-center" style={{ width: 80, fontSize: "0.875rem", fontWeight: 400, color: "#374151" }}>Assigned</th>
                                            <th className="border-0 pt-1 pb-2 text-center" style={{ width: 80, fontSize: "0.875rem", fontWeight: 400, color: "#374151" }}>In Progress</th>
                                            <th className="border-0 pt-1 pb-2 text-center" style={{ width: 80, fontSize: "0.875rem", fontWeight: 400, color: "#374151" }}>Review</th>
                                            <th className="border-0 pt-1 pb-2 text-center" style={{ width: 80, fontSize: "0.875rem", fontWeight: 400, color: "#374151" }}>Completed</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {teamStatus?.map((u, idx) => (
                                            <tr key={idx} style={{ borderBottom: idx < 4 ? "1px solid #f3f4f6" : "none" }}>
                                                <td className="border-0 py-3">
                                                    <div className="d-flex align-items-center">
                                                        <div className="d-inline-flex align-items-center justify-content-center rounded-circle me-3"
                                                            style={{ width: 40, height: 40, background: "#f1f3f5", color: "#495057", fontWeight: 500 }}>
                                                            {getInitials(u?.name)}
                                                        </div>
                                                        <span className="fw-normal" style={{ color: "#374151" }}>{u.name}</span>
                                                    </div>
                                                </td>
                                                <td className="border-0 py-3 text-center" style={{ borderRight: "1px solid #e5e7eb" }}>
                                                    <span className={"text-dark"} style={{ fontWeight: 500 }}>{u.todo ?? 0}</span>
                                                </td>
                                                <td className="border-0 py-3 text-center">
                                                    <span className="text-dark" style={{ fontWeight: 500 }}>{u.inProgress ?? 0}</span>
                                                </td>
                                                <td className="border-0 py-3 text-center">
                                                    <span className="text-dark" style={{ fontWeight: 500 }}>{u.review ?? 0}</span>
                                                </td>
                                                <td className="border-0 py-3 text-center">
                                                    <span className="text-dark" style={{ fontWeight: 500 }}>{u.completed ?? 0}</span>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                        <div>
                        </div>
                    </div>
                </div>
                <TaskListSection
                    className="mt-4"
                    title={"High Priority Tasks Requiring Attention"}
                    items={highPriority} />
                <div
                    className="shadow-sm mt-3"
                    style={{
                        borderRadius: 12,
                        border: "1px solid #edf0f4",
                        background: "#fff",
                        overflow: "hidden",
                    }}
                >
                    <div className="p-3 pb-2 d-flex align-items-center" style={{ color: "#374151" }}>
                        <GoAlertFill size={22} className="me-2" style={{ color: "#dc3545" }} />
                        <h5 className="mb-0 fs-3 fw-semibold"> Overdue Work Items </h5>
                        <span
                            className="ms-2 d-inline-flex align-items-center justify-content-center"
                            style={{
                                width: 28,
                                height: 28,
                                borderRadius: 999,
                                background: "#dc3545",
                                color: "#fff",
                                fontWeight: 700,
                                fontSize: 14,
                            }}
                        >
                            {cards.overdueTasks || 0}
                        </span>
                    </div>
                    <div className="px-3 pb-3">
                        <div className="table-responsive">
                            <table className="table align-middle mb-0">
                                <thead>
                                    <tr style={{ borderBottom: "1px solid #e5e7eb" }}>
                                        <th className="border-0 pb-2 text-start" style={{ width: 200, color: "#374151", fontWeight: 500 }}>Tasks</th>
                                        <th className="border-0 pb-2 text-center" style={{ color: "#374151", fontWeight: 500, }}>Assignee</th>
                                        <th className="border-0 pb-2 text-end" style={{ color: "#374151", fontWeight: 500 }}>Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {overdueTasksData?.length > 0 ? (
                                        overdueTasksData?.map((task, idx) => (
                                            <tr key={task.id} style={{ borderBottom: idx < overdueTasksData.length - 1 ? "1px solid #f3f4f6" : "none" }}>
                                                <td className="border-0 py-3">
                                                    <div className="d-flex align-items-center">
                                                        <div
                                                            className="d-inline-flex align-items-center justify-content-center rounded-circle me-3"
                                                            style={{ width: 40, height: 40, background: "#f1f3f5", color: "#495057", fontWeight: 500 }}
                                                        >
                                                            {getInitials(task?.title)}
                                                        </div>
                                                        <div className='d-flex flex-column text-start'>
                                                            <span className="fw-semibold ms-3" style={{ color: "#374151", fontSize: "16px" }}>{task.title}</span>
                                                            <div className='d-flex flex-row align-items-center gap-2 mt-1'>
                                                                <TaskBadge text={task.priority} variant={task.priority} />
                                                                <span className="text-muted" style={{ fontSize: "12px" }}>
                                                                    Due: {task.dueDate ? formatDate(task?.dueDate ?? "") : 'No due date'}
                                                                </span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="border-0 py-3 d-flex align-items-center justify-content-center" style={{ borderRight: "1px solid #e5e7eb" }}>
                                                    <div className="ps-4 d-flex align-items-center ms-3">
                                                        <div
                                                            className="d-inline-flex align-items-center justify-content-center rounded-circle me-3"
                                                            style={{ width: 40, height: 40, background: "#f1f3f5", color: "#495057", fontWeight: 500 }}
                                                        >
                                                            {getInitials(task?.assignee)}
                                                        </div>
                                                        <div className='d-flex flex-column text-start'>
                                                            <span className="fw-semibold" style={{ color: "#374151", fontSize: "14px" }}>{task.assignee}</span>
                                                            {task.assigneeEmail && (
                                                                <span className='fw-normal text-muted' style={{ fontSize: "12px" }}>{task.assigneeEmail}</span>
                                                            )}
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="border-0 py-3 text-end" style={{ borderRight: "1px solid #e5e7eb" }}>
                                                    <div className='d-flex flex-column'>
                                                        <span className="text-danger fw-semibold" style={{ fontSize: "14px" }}>
                                                            {task.daysOverdue > 0 ? `Late by ${task.daysOverdue} days` : 'Due today'}
                                                        </span>
                                                        <h5 className="mb-0 mt-1"><span className='badge text-bg-secondary' style={{ fontSize: "12px" }}>{task.status}</span></h5>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan="3" className="border-0 py-4 text-center text-muted">
                                                No overdue tasks found
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                    <div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default Dashboard;
